/**
 * SlickGrid Toolbar Utility Functions
 *
 * Pure utility functions for toolbar operations that can be tested independently.
 */

// ============================================================================
// TOOLBAR ACTION IDS
// ============================================================================

/**
 * Action id emitted by the toolbar's properties-panel toggle via `toolbarClickHandler`.
 * Shared so consumers (e.g. SingleTransmittal) match on this constant instead of a
 * stringly-typed `"properties"` literal that a typo would silently break.
 */
export const TOOLBAR_PROPERTIES_ACTION = "properties";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type RowSizeKey = "small" | "medium" | "large" | "wrap";

export interface ToolbarItem {
  id: string;
  name?: string;
  rightAllign?: boolean;
  sticky?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  tooltip?: string;
  template?: (props: Record<string, unknown>) => React.ReactNode;
  props?: Record<string, unknown>;
  buttonType?: string;
  cssClass?: string;
  prefixIcon?: string;
  suffixIcon?: string;
  prefixIconTemplate?: React.ReactNode;
  suffixIconTemplate?: React.ReactNode;
}

export interface ToolbarArrays {
  leftArray: ToolbarItem[];
  rightArray: ToolbarItem[];
  leftStickyArray: ToolbarItem[];
  rightStickyArray: ToolbarItem[];
}

// ============================================================================
// ROW SIZE ICON MAPPING
// ============================================================================

const ROW_SIZE_ICON_MAP: Record<RowSizeKey, string> = {
  small: "RowResizeIcon",
  medium: "MediumCell",
  large: "LargeRowSize",
  wrap: "WrapText",
};

/**
 * Get the icon name for a given row size
 */
export const getRowIconName = (size: RowSizeKey): string => {
  return ROW_SIZE_ICON_MAP[size] || ROW_SIZE_ICON_MAP.large;
};

/**
 * Validate if a string is a valid row size key
 */
export const isValidRowSize = (size: string): size is RowSizeKey => {
  return size === "small" || size === "medium" || size === "large" || size === "wrap";
};

// ============================================================================
// TOOLBAR ITEM FILTERING
// ============================================================================

/**
 * Filter toolbar items into categorized arrays based on alignment and sticky properties
 */
export const categorizeToolbarItems = (toolbar: ToolbarItem[]): ToolbarArrays => {
  if (!toolbar || !Array.isArray(toolbar)) {
    return {
      leftArray: [],
      rightArray: [],
      leftStickyArray: [],
      rightStickyArray: [],
    };
  }

  return {
    leftArray: toolbar.filter((item) => !item.rightAllign && !item.sticky),
    rightArray: toolbar.filter((item) => item.rightAllign && !item.sticky),
    leftStickyArray: toolbar.filter((item) => !item.rightAllign && item.sticky),
    rightStickyArray: toolbar.filter((item) => item.rightAllign && item.sticky),
  };
};

/**
 * Check if a toolbar item should be rendered (not hidden)
 */
export const isToolbarItemVisible = (item: ToolbarItem): boolean => {
  return !item.hidden;
};

/**
 * Check if a toolbar item is interactive (not disabled)
 */
export const isToolbarItemEnabled = (item: ToolbarItem): boolean => {
  return !item.disabled;
};

/**
 * Get visible toolbar items from an array
 */
export const getVisibleToolbarItems = (items: ToolbarItem[]): ToolbarItem[] => {
  return items.filter(isToolbarItemVisible);
};

/**
 * Get enabled toolbar items from an array
 */
export const getEnabledToolbarItems = (items: ToolbarItem[]): ToolbarItem[] => {
  return items.filter(isToolbarItemEnabled);
};

// ============================================================================
// OVERFLOW DETECTION
// ============================================================================

/**
 * Check if an element has horizontal overflow
 */
export const hasHorizontalOverflow = (element: HTMLElement | null): boolean => {
  if (!element) return false;
  return element.scrollWidth > element.clientWidth;
};

/**
 * Check if an element is scrolled to the start (left in LTR)
 */
export const isScrolledToStart = (element: HTMLElement | null): boolean => {
  if (!element) return true;
  return element.scrollLeft === 0;
};

/**
 * Check if an element is scrolled to the end (right in LTR)
 */
export const isScrolledToEnd = (element: HTMLElement | null): boolean => {
  if (!element) return true;
  // Allow 1px tolerance for rounding errors
  return Math.abs(element.scrollWidth - element.scrollLeft - element.clientWidth) <= 1;
};

/**
 * Calculate scroll amount for arrow navigation
 */
export const getScrollAmount = (direction: "forward" | "backward", amount: number = 200): number => {
  return direction === "forward" ? amount : -amount;
};

// ============================================================================
// CONDITION BADGE VISIBILITY
// ============================================================================

/**
 * Check if a badge should be shown for active conditions
 */
export const shouldShowConditionBadge = (conditions: unknown[] | undefined | null): boolean => {
  return Array.isArray(conditions) && conditions.length > 0;
};

// ============================================================================
// DIVIDER VISIBILITY
// ============================================================================

/**
 * Check if the divider between filter/sort/group and column chooser should be shown
 */
export const shouldShowDivider = (options: {
  enableGroupingOption?: boolean;
  enableFilteringOption?: boolean;
  enableSortingOption?: boolean;
  enableColumnChooser?: boolean;
  propertiesPanel?: boolean;
}): boolean => {
  const hasFilterSortGroup =
    options.enableGroupingOption || options.enableFilteringOption || options.enableSortingOption;
  const hasColumnChooserOrProperties = options.enableColumnChooser || options.propertiesPanel;
  return Boolean(hasFilterSortGroup && hasColumnChooserOrProperties);
};

// ============================================================================
// EXPORT LOGIC
// ============================================================================

export interface ExportConfig {
  remoteDataSource?: boolean;
  infiniteScroll?: boolean;
  hasGroupConditions: boolean;
}

export type ExportMethod = "remote" | "grouped" | "standard";

/**
 * Determine which export method to use based on configuration
 */
export const getExportMethod = (config: ExportConfig): ExportMethod => {
  if (config.remoteDataSource && config.infiniteScroll) {
    return "remote";
  }
  if (config.hasGroupConditions) {
    return "grouped";
  }
  return "standard";
};

// ============================================================================
// COLUMN CHOOSER HELPERS
// ============================================================================

/**
 * Validate that at least one column is selected
 */
export const validateColumnSelection = (selectedColumns: unknown[]): boolean => {
  return Array.isArray(selectedColumns) && selectedColumns.length > 0;
};

/**
 * Build a map of column widths from existing columns
 */
export const buildColumnWidthMap = (columns: Array<{ id: string; width?: number }>): Record<string, number> => {
  return columns.reduce(
    (widths, col) => {
      if (col.id && col.width !== undefined) {
        widths[col.id] = col.width;
      }
      return widths;
    },
    {} as Record<string, number>,
  );
};

// ============================================================================
// MODAL STATE MANAGEMENT
// ============================================================================

export type ModalState = "" | "Filter" | "Group" | "Sort";

/**
 * Check if a modal is currently open
 */
export const isModalOpen = (modalState: ModalState): boolean => {
  return modalState !== "";
};

/**
 * Get the modal type that is currently open
 */
export const getActiveModal = (modalState: ModalState): ModalState => {
  return modalState;
};

/**
 * Check if a specific modal is open
 */
export const isSpecificModalOpen = (modalState: ModalState, targetModal: ModalState): boolean => {
  return modalState === targetModal && targetModal !== "";
};
